'use client';

import FlagIcon, { hasFlag, useFlagVisible } from './FlagIcon';
import BrandMark from './BrandMark';
import type { InputLanguage } from '@/lib/modeText';

/**
 * Icons for the mode column.
 *
 * The registry sends an icon *name*; this maps names to SVG. A mode whose icon
 * is unknown (or absent) falls back to its initials, so adding a mode on the
 * backend still works with no frontend change — it just looks plainer until an
 * icon is added here.
 *
 * The flag is drawn as plain geometry rather than an emoji on purpose: Windows
 * does not render flag emoji at all — it shows the letters "US" — which is
 * exactly the lettering this was meant to replace.
 */

function Arrow() {
  // Translate: text moving from one side to the other.
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 12h15m0 0l-5.5-5.5M19 12l-5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Graduation() {
  /*
   * A1 English: a beginner, at a book.
   *
   * This was a mortarboard, which says the opposite of what the mode is for — a
   * graduation cap means finished, and A1 is the first rung. A learner bent over
   * an open book is the beginner, and it keeps working for A2 and B1, which
   * share this icon.
   *
   * The name stays `graduation` because that is the key the backend registry
   * sends; renaming it would mean a coordinated change across two services for
   * no gain.
   */
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6">
      {/* head + shoulders, small and low, so the figure reads as a young learner */}
      <circle cx="12" cy="5.4" r="2.6" />
      <path d="M8.2 12.4a3.9 3.9 0 017.6 0" strokeLinecap="round" />
      {/* the open book they are leaning over */}
      <path d="M3.2 15.2c2.9-1 5.9-1 8.8 0 2.9-1 5.9-1 8.8 0v4.6c-2.9-1-5.9-1-8.8 0-2.9-1-5.9-1-8.8 0v-4.6z"
            strokeLinejoin="round" />
      <path d="M12 15.2v4.6" />
    </svg>
  );
}

function Lightbulb() {
  /*
   * Explain: a hand pointing at the thing being explained.
   *
   * A lightbulb is the idea arriving, which is what the reader does; the mode's
   * job is to point at the part they would otherwise miss. Pointing right also
   * aims at the output pane this mode fills, which the bulb did not.
   *
   * Key stays `lightbulb` — it is what the backend registry sends.
   */
  /*
   * Drawn pointing UP and rotated a quarter turn, rather than drawn pointing
   * right. A hand is a shape people recognise from its silhouette, and the
   * upright one is the silhouette they know; hand-fitting the curves sideways
   * produced something that read as a fist with a spike. Rotating a correct
   * shape keeps it correct.
   */
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor"
         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <g transform="rotate(90 12 12)">
        {/* index finger — the long one, and after the rotation the one aimed at
            the output pane this mode fills */}
        <path d="M10 9.5V4a2 2 0 014 0v6" />
        {/* the other three, each folded a little shorter than the last */}
        <path d="M14 10V6.5a2 2 0 014 0V11" />
        <path d="M18 11v-.5a2 2 0 014 0V14" />
        {/* thumb, palm and wrist */}
        <path d="M22 14a8 8 0 01-8 8h-2c-2.8 0-4.5-.9-6-2.3l-3.6-3.6a2 2 0 012.8-2.8L7 15V4a2 2 0 014 0" />
      </g>
    </svg>
  );
}

function Proofread() {
  /*
   * Mistakes: an error badge.
   *
   * It was lines of text with a tick, which is the wrong signal twice over — a
   * tick means "passed", and drawn in `currentColor` it looked like every other
   * mode in the column. This is the one mode that reports something is WRONG,
   * so it is the one that gets a colour.
   *
   * Solid red fill rather than a red outline, because the button behind it has
   * three different backgrounds: white, near-black, and the indigo of the
   * selected state. A stroked red glyph would be legible on the first two and
   * muddy on the third; a filled badge carries its own background and reads on
   * all of them. That is also why the mark inside is white rather than
   * `currentColor` — currentColor goes white when the button is selected, and
   * the exclamation would vanish into the badge.
   */
  return (
    <svg viewBox="0 0 24 24" className="h-[19px] w-[19px]" aria-hidden>
      <circle cx="12" cy="12" r="9.25" fill="#ef4444" />
      <path
        d="M12 6.9v6.2"
        stroke="#fff"
        strokeWidth="2.3"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16.75" r="1.28" fill="#fff" />
    </svg>
  );
}

function Sparkle() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" strokeLinejoin="round" />
      <path d="M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z" strokeLinejoin="round" />
    </svg>
  );
}

function Tie() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M9 3h6l-1.5 3.5h-3L9 3z" strokeLinejoin="round" />
      <path d="M10.5 6.5L9 14l3 7 3-7-1.5-7.5" strokeLinejoin="round" />
    </svg>
  );
}

function Smile() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 007 0" strokeLinecap="round" />
      <path d="M9 9.5h.01M15 9.5h.01" strokeLinecap="round" strokeWidth="2.2" />
    </svg>
  );
}

/** Stars and stripes, simplified to what reads at 18px. */
function FlagUs() {
  const stripe = 14 / 13;
  return (
    <svg viewBox="0 0 24 14" className="h-[14px] w-6 shrink-0 rounded-[2px] ring-1 ring-black/10">
      <rect width="24" height="14" fill="#fff" />
      {[0, 2, 4, 6, 8, 10, 12].map((i) => (
        <rect key={i} y={i * stripe} width="24" height={stripe} fill="#B22234" />
      ))}
      <rect width="10.5" height={stripe * 7} fill="#3C3B6E" />
      {[0, 1, 2].map((r) =>
        [0, 1, 2, 3].map((c) => (
          <circle
            key={`${r}-${c}`}
            cx={1.6 + c * 2.5 + (r % 2 ? 1.25 : 0)}
            cy={1.4 + r * 2.2}
            r="0.55"
            fill="#fff"
          />
        )),
      )}
    </svg>
  );
}

/**
 * The flag of the language the user is writing in — the Registers button.
 *
 * It used to be the Stars and Stripes, permanently, because the mode used to be
 * "US English". Now the mode rewrites whatever language came in, so a fixed flag
 * would be a promise the button does not keep: it is drawn from the input
 * language's first country (`Language.countries`, the same data the picker uses)
 * and changes as you type.
 *
 * Three cases, in order:
 *
 *   • a language whose flag this person SHOWS gets its flag.
 *   • a language whose flag they have switched off in Settings ▸ Flags gets the
 *     site's own mark instead. This is a button and a button needs a face, so
 *     it is the one place a hidden flag cannot simply leave a hole. It is also
 *     literally what Korean looked like before the setting existed — the flag
 *     was missing from the registry then rather than switched off, and the mark
 *     stood in for it — which is why turning `kr` off still produces exactly
 *     the button everybody here is used to.
 *   • no language known yet — an empty box, or Latin text the detector has not
 *     settled — gets a globe. Neutral, and it never claims a language that the
 *     next keystroke could disprove. A language with no artwork in the sprite
 *     lands here too, which is right: nobody chose that, we simply have no
 *     picture, and the mark would be claiming a preference that was never
 *     expressed.
 */
function InputFlag({ lang }: { lang: InputLanguage | null }) {
  const visible = useFlagVisible();
  if (lang?.country && visible(lang.country)) {
    return (
      <FlagIcon
        code={lang.country}
        className="h-[14px] w-[19px] rounded-[2px] ring-1 ring-black/15 dark:ring-white/20"
      />
    );
  }
  if (lang?.country && hasFlag(lang.country)) {
    // Switched off. The header's mark, at 19px — and through BrandMark, so the
    // light theme gets the light copy here too. This chip sits on the pane's own
    // background, which is the surface the plain artwork disappears on.
    return <BrandMark className="h-[19px] w-[19px] shrink-0 rounded-[3px] object-cover" />;
  }
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" strokeLinecap="round" />
      <path d="M12 3a14 14 0 010 18a14 14 0 010-18z" strokeLinejoin="round" />
    </svg>
  );
}

function Human() {
  /*
   * Humanize: a robot.
   *
   * It was a person, which named the destination. The robot names the PROBLEM —
   * this is the mode you reach for because the text reads like a machine wrote
   * it — and that is the thing someone scanning the column is looking for.
   *
   * Key stays `human`, which is what the backend registry sends.
   */
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6">
      {/* antenna */}
      <path d="M12 3v2.6" strokeLinecap="round" />
      <circle cx="12" cy="2.4" r="1.15" />
      {/* head */}
      <rect x="4" y="5.6" width="16" height="12" rx="3.2" />
      {/* eyes, filled so they read at 18px where two more outlines would not */}
      <circle cx="9" cy="11" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="15" cy="11" r="1.35" fill="currentColor" stroke="none" />
      {/* mouth */}
      <path d="M9.2 14.6h5.6" strokeLinecap="round" />
      {/* ears */}
      <path d="M2.2 10v3.2M21.8 10v3.2" strokeLinecap="round" />
    </svg>
  );
}

function Gem() {
  /*
   * AI Prompt: a cut gem.
   *
   * The mode takes the rough thing you wrote and returns the finished, faceted
   * version of it, which is what cutting a stone is — so the icon is the
   * metaphor rather than a decoration on top of one. It also has to survive at
   * 18px next to a robot and an arrow: a wide table facet, two crown facets
   * meeting at the point, and nothing else. Anything more became a grey blob.
   *
   * ── Why this one is in colour, and alone in that ──────────────────────────
   *
   * Every other mark in the column is a currentColor line drawing, painted grey
   * at rest and given its colour by being hovered or chosen (see the note on
   * the filter in ModeColumn). This one carries its own colour all the time,
   * for the reason the column's rule exists at all: colour means "look here",
   * and this is the newest mode, the one nobody has a habit for yet.
   *
   * It is also the only icon whose subject IS colour. A diamond has no local
   * colour of its own — what you see in one is fire, white light split into
   * spectral flashes by the facets — so a monochrome diamond is a drawing of a
   * piece of glass. The facets below are lit as a real stone is: cool at the
   * table where the light goes in, warm and saturated in the pavilion where it
   * comes back out, and no two adjacent facets the same hue.
   *
   * The outline is the thing that had to give. Drawn at the same 1.6 as its
   * monochrome neighbours it was a black diamond with a faint wash inside —
   * technically coloured, and read by the eye as another line icon. It is now
   * thinner and half-transparent: enough to hold the facet seams together and
   * to separate the stone from whatever is behind it, not enough to be the
   * loudest thing in the drawing. currentColor still, so it is ink over the
   * page at rest and white over the accent blue when the mode is chosen.
   */
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.15"
         strokeOpacity="0.55" strokeLinecap="round" strokeLinejoin="round">
      <defs>
        {/*
          Fixed ids, not generated ones. SVG paint servers are looked up in the
          whole document, so two copies of this icon on one page would share the
          first set of stops — which is harmless here precisely because the ids
          are fixed and every copy defines the SAME stops. Generated ids would
          buy nothing and would differ between the server render and the
          client's, which is a hydration mismatch for no gain.
        */}
        <linearGradient id="gt-gem-table" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bae6fd" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
        <linearGradient id="gt-gem-crown-l" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
        <linearGradient id="gt-gem-crown-r" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fda4af" />
          <stop offset="100%" stopColor="#f43f5e" />
        </linearGradient>
        <linearGradient id="gt-gem-pav-l" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0284c7" />
        </linearGradient>
        <linearGradient id="gt-gem-pav-c" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#818cf8" />
          <stop offset="100%" stopColor="#4f46e5" />
        </linearGradient>
        <linearGradient id="gt-gem-pav-r" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f0abfc" />
          <stop offset="100%" stopColor="#c026d3" />
        </linearGradient>
      </defs>

      {/*
        The facets, filled and unstroked, laid down first so the outline below
        draws over their seams rather than under them. The geometry is the same
        girdle (y = 9.5) and the same two crown lines (x = 9 and x = 15) that
        the outline uses, so the fills and the drawing cannot drift apart.
      */}
      <g stroke="none">
        {/* crown: shoulder, table, shoulder */}
        <path d="M3.4 9.5L7.4 4.5 9 9.5z" fill="url(#gt-gem-crown-l)" />
        <path d="M7.4 4.5h9.2L15 9.5H9z" fill="url(#gt-gem-table)" />
        <path d="M16.6 4.5l4 5H15z" fill="url(#gt-gem-crown-r)" />
        {/* pavilion: the three facets that carry the light down to the culet */}
        <path d="M3.4 9.5H9l3 10.2z" fill="url(#gt-gem-pav-l)" />
        <path d="M9 9.5h6l-3 10.2z" fill="url(#gt-gem-pav-c)" />
        <path d="M15 9.5h5.6L12 19.7z" fill="url(#gt-gem-pav-r)" />
      </g>

      {/* the outline: table across the top, two shoulders, culet at the bottom */}
      <path d="M7.4 4.5h9.2l3.4 5-8 10.2-8-10.2 3.4-5z" />
      {/* the girdle — the line that makes it read as cut rather than as a kite */}
      <path d="M3.4 9.5h17.2" />
      {/* the two facets that carry the light down to the point */}
      <path d="M7.4 4.5l1.6 5 3 10.2M16.6 4.5l-1.6 5-3 10.2" />
    </svg>
  );
}

function Notebook() {
  /*
   * The Notebook: a bound book, spine on the left, with its rings showing.
   *
   * Deliberately NOT a folder, which is the obvious drawing and the wrong one —
   * a folder is one of the things INSIDE this, and using it for the whole would
   * make the button look like "open a folder" rather than "open the place where
   * your folders are". The rings are what say bound rather than loose: they are
   * the difference between a book and a sheet of paper, at 18px.
   *
   * ── Why this one has colours of its own ────────────────────────────────────
   * Every other mark on the rail is `currentColor`, and the column desaturates
   * them at rest so that colour means "this one" rather than "this one is
   * special" (see ModeColumn). Two marks are exempt and both are exempt for the
   * same reason: the gem, because a desaturated diamond is a drawing of glass,
   * and this, because it is the newest thing on the rail and the only one that
   * is not a mode. It opens a window over the page instead of changing what
   * happens to the text, and a rail of six identical grey marks gives a reader
   * no reason to suspect that the seventh does something else entirely.
   *
   * Amber cover, accent-blue rings: the brand's two colours, and the pairing
   * that reads as a notebook rather than as another line icon.
   */
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]">
      {/* the cover, filled — this is the part that carries the colour */}
      <path
        d="M6.5 3.5h11a1 1 0 011 1v15a1 1 0 01-1 1h-11a2 2 0 01-2-2v-13a2 2 0 012-2z"
        className="fill-amber-400/85 stroke-amber-600 dark:fill-amber-400/70 dark:stroke-amber-300"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      {/* the written page, so the cover reads as a book and not as a card */}
      <path
        d="M9 8.5h6M9 12h6M9 15.5h3.5"
        className="stroke-amber-800/70 dark:stroke-amber-900/80"
        strokeWidth="1.3"
        strokeLinecap="round"
        fill="none"
      />
      {/* the rings on the spine */}
      <path
        d="M4.5 8.5h3M4.5 12h3M4.5 15.5h3"
        className="stroke-accent-600 dark:stroke-accent-400"
        strokeWidth="1.7"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

const ICONS: Record<string, () => JSX.Element> = {
  arrow: Arrow,
  graduation: Graduation,
  lightbulb: Lightbulb,
  proofread: Proofread,
  sparkle: Sparkle,
  tie: Tie,
  smile: Smile,
  human: Human,
  gem: Gem,
  'flag-us': FlagUs,
  notebook: Notebook,
};

export default function ModeIcon({
  name,
  label,
  lang,
}: {
  name: string | null;
  label: string;
  /** The language being written, for the icons that follow it. */
  lang?: InputLanguage | null;
}) {
  // Handled before the table because this one is not a fixed piece of artwork —
  // it is a function of what is in the input box.
  if (name === 'flag-input') return <InputFlag lang={lang ?? null} />;
  const Icon = name ? ICONS[name] : undefined;
  if (Icon) return <Icon />;
  // Unknown icon — initials, so a backend-only mode still renders something.
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return <span className="text-[11px] font-semibold tracking-wide">{initials}</span>;
}
